import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";

import { InfiniteScroll, InfiniteTable } from "../../components";
import { UserContext } from "../../contexts/index.jsx";
import { hasCrmVisibility } from "../../helpers/adminAccess";
import { useStoreWriteGuard } from "../../helpers/storeScope";
import { StatePanel } from "../../components/ui";
import { USER_UPDATED_KEY, fetchUserById, parseUserUpdatedValue } from "../../helpers/userEditTab";
import { ContactRow } from "./index.jsx";
import DirectoryList from "./DirectoryList.jsx";

const DIRECTORY_NOUN = { client: "clients", merchant: "merchants", expert: "experts" };

/**
 * Fontaine M54E — the `/users/:type?` directory RESULT SURFACE is a stacked LIST
 * (DirectoryList.jsx), not a table: no column headers, no banding, one row per
 * person. Was M55A's shared `DataTable`, whose column vocabulary lived here.
 * Nothing about the request, the gating, the pagination or the row's own action
 * (opening the contact) changed — see
 * `tests/views/UsersDirectoryTableContract.test.jsx`.
 */

/**
 * A row was a frozen snapshot of the search hit, so editing a user in the edit tab left a stale
 * name/email in the list until it remounted (see Row.jsx's per-row `useUserUpdates` on the picker
 * path). `DataTable`'s `cell(row, meta)` is a render FUNCTION, not a component — a row can no
 * longer own that hook itself — so the same storage-event listener is hoisted to cover every
 * visible row at once, one listener instead of one per row. Storage-event only, exactly as before:
 * a focus refetch here would fire one request per row.
 */
const useFreshenedItems = (items) => {
  const [patches, setPatches] = useState({});
  const idsRef = useRef(new Set());

  useEffect(() => {
    idsRef.current = new Set(items.map((item) => item.id));
  }, [items]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== USER_UPDATED_KEY) return;
      const id = parseUserUpdatedValue(e.newValue);
      if (id == null || !idsRef.current.has(id)) return;
      fetchUserById(id)
        .then((fresh) => {
          if (!fresh) return;
          setPatches((prev) => ({ ...prev, [id]: { ...prev[id], ...fresh } }));
        })
        .catch(() => {});
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return useMemo(
    () => items.map((item) => (patches[item.id] ? { ...item, ...patches[item.id] } : item)),
    [items, patches]
  );
};

/** The DataTable / empty-text switch, as its own component so `useFreshenedItems` is a proper
 * hook call (owned by ITS render, not smuggled into `InfiniteScroll`'s via an inline callback). */
const DirectoryResultsBody = ({
  items,
  isLoading,
  emptyText,
  empty,
  type,
  noun,
  onOpenContact,
  selectedContactId,
  view,
}) => {
  const freshened = useFreshenedItems(items);

  if (freshened.length > 0) {
    return (
      <DirectoryList
        contacts={freshened}
        type={type}
        noun={noun}
        selectedContactId={selectedContactId}
        onOpenContact={onOpenContact}
        view={view}
      />
    );
  }
  if (!isLoading) {
    if (empty) return empty;
    return (
      <div className="no-results">
        <p>{emptyText}</p>
      </div>
    );
  }
  return null;
};

const DirectoryResults = ({
  fetchUrl,
  fetchParams,
  fetchLimit = 20,
  emptyText,
  empty,
  type,
  noun,
  onOpenContact,
  selectedContactId,
  gateUntilSearch,
  minSearchLength,
  view,
}) => (
  <InfiniteScroll
    fetchUrl={fetchUrl}
    fetchParams={fetchParams}
    fetchLimit={fetchLimit}
    gateUntilSearch={gateUntilSearch}
    minSearchLength={minSearchLength}
  >
    {({ items, isLoading }) => (
      <DirectoryResultsBody
        items={items}
        isLoading={isLoading}
        emptyText={emptyText}
        empty={empty}
        type={type}
        noun={noun}
        onOpenContact={onOpenContact}
        selectedContactId={selectedContactId}
        view={view}
      />
    )}
  </InfiniteScroll>
);

const ContactTable = ({
  searchQuery,
  sort = { field: "created_at", direction: "desc" },
  filters = {},
  onSelectClick,
  onOpenContact,
  selectedContactId,
  fetchLimit,
  addLoggedUserFirst = false,
  minSearchLength = 0,
  pickerLayout,
  view = "list",
}) => {
  const [refresh, setRefresh] = useState(0);
  const { user } = useContext(UserContext);
  const { guardProps: storeGuard } = useStoreWriteGuard();
  const type = filters?.type || "client";

  const gated = !hasCrmVisibility(user);
  const trimmedQuery = String(searchQuery ?? "").trim();
  const isSearching = !!trimmedQuery;
  const belowMin = trimmedQuery.length > 0 && trimmedQuery.length < minSearchLength;
  const noun = DIRECTORY_NOUN[type] || "users";
  const emptyText = belowMin
    ? `Type at least ${minSearchLength} characters to search.`
    : gated && !isSearching
      ? "Search by name, contact, or store name to begin."
      : `No ${noun} found.`;

  if (onSelectClick) {
    const headers = [
      { label: "User", className: "col-user" },
      { label: "Contact", className: "col-contact" },
      { label: "", className: "col-action" },
    ];

    return (
      <InfiniteTable
        key={refresh}
        className="contacts users-table mobile-alternative"
        fetchUrl="/api/users/search"
        responsive={false}
        fetchParams={{ query: searchQuery, sort, filters }}
        fetchLimit={fetchLimit}
        emptyText={emptyText}
        headers={headers}
        onSelectClick={onSelectClick}
        RowComponent={ContactRow}
        rowComponentProps={{ setRefresh, onOpenContact, selectedContactId, pickerLayout }}
        addLoggedUserFirst={addLoggedUserFirst}
        gateUntilSearch={gated}
        minSearchLength={minSearchLength}
      />
    );
  }

  // F5: a store with no clients yet gets one friendly empty state with one action. Gated
  // (search-first) users, searches and the status / KYC quick filters keep today's text.
  // "Add a client" wears the same storeGuard as the page's "+ Create user".
  const unfilteredClients =
    type === "client" &&
    !gated &&
    !isSearching &&
    (!filters?.extra_filter || filters.extra_filter === "show_all") &&
    !filters?.pending_id_verification;
  const empty = unfilteredClients ? (
    <StatePanel
      state="empty"
      title="No clients yet"
      description="Add a client to start tracking their requests and purchases."
      action={{ label: "Add a client", to: "/users/create", ...storeGuard }}
    />
  ) : undefined;

  return (
    <DirectoryResults
      key={refresh}
      fetchUrl="/api/users/search"
      fetchParams={{ query: searchQuery, sort, filters }}
      fetchLimit={fetchLimit}
      emptyText={emptyText}
      empty={empty}
      type={type}
      noun={noun}
      onOpenContact={onOpenContact}
      selectedContactId={selectedContactId}
      gateUntilSearch={gated}
      minSearchLength={minSearchLength}
      view={view}
    />
  );
};

export default ContactTable;
